import { IBusinessEntity } from '@commenda/types';
import {
  Month,
  MonthWithMonthValue,
  Quarter,
} from '@commenda/types/src/compliance-calendar/compliances/types';
import { IDocument, INewFolder } from '@commenda/types/src/drive-view/types';
import { DocumentFolderTypes } from '@commenda/types/src/random';
import { toTitleCaseSentence } from './methods';

function getMonthAbbreviation(dateStr: string): Month {
  const date = new Date(dateStr);
  const monthIndex = date.getMonth();

  // Convert zero-based month index to 1-based index
  const monthNumber = monthIndex + 1;

  // Use the MonthWithMonthValue object to get the abbreviation
  return MonthWithMonthValue[monthNumber];
}

export function isCurrentDateInQuarter(
  quarter: Quarter,
  businessEntity: IBusinessEntity
): boolean {
  const now = new Date();

  // If corporationData or relevant quarter information is missing, return false
  if (
    !businessEntity ||
    !businessEntity.featureSpecificInfo?.compliance?.complianceCalendar ||
    !businessEntity.featureSpecificInfo?.compliance?.complianceCalendar
      ?.quarters?.[quarter]
  ) {
    return false;
  }

  const { effectiveStartDate, effectiveEndDate } =
    businessEntity.featureSpecificInfo?.compliance?.complianceCalendar
      ?.quarters?.[quarter];

  // Parse the start and end dates
  const start = new Date(effectiveStartDate);

  // Check if the current date is in or has passed the quarter
  return now >= start;
}

export function getMISReportsForCurrentFY(driveView: INewFolder) {
  const filesBasedOnQuarters: Record<Quarter, IDocument[]> = {
    [Quarter.Q1]: [],
    [Quarter.Q2]: [],
    [Quarter.Q3]: [],
    [Quarter.Q4]: [],
  };
  const currentYearQuartersFolder = driveView?.folders
    ?.map((folder) => {
      if (folder?.id?.startsWith(DocumentFolderTypes.misReports.name))
        return folder?.folders;
    })
    ?.flat()
    ?.find(
      (fiscalYearFolder) =>
        fiscalYearFolder?.id ===
        `${DocumentFolderTypes.misReports.name}_FY${new Date().getFullYear()}`
    );

  currentYearQuartersFolder?.folders?.map((quarterFolder) => {
    if (
      quarterFolder.id ===
      `${DocumentFolderTypes.misReports.name}_FY${new Date().getFullYear()}_${DocumentFolderTypes.misReports.Q1}`
    )
      filesBasedOnQuarters.Q1.push(...quarterFolder.files);
    if (
      quarterFolder.id ===
      `${DocumentFolderTypes.misReports.name}_FY${new Date().getFullYear()}_${DocumentFolderTypes.misReports.Q2}`
    )
      filesBasedOnQuarters.Q2.push(...quarterFolder.files);
    if (
      quarterFolder.id ===
      `${DocumentFolderTypes.misReports.name}_FY${new Date().getFullYear()}_${DocumentFolderTypes.misReports.Q3}`
    )
      filesBasedOnQuarters.Q3.push(...quarterFolder.files);
    if (
      quarterFolder.id ===
      `${DocumentFolderTypes.misReports.name}_FY${new Date().getFullYear()}_${DocumentFolderTypes.misReports.Q4}`
    )
      filesBasedOnQuarters.Q4.push(...quarterFolder.files);
  });

  return filesBasedOnQuarters;
}

export function getFinancialYearQuarters(businessEntity: IBusinessEntity) {
  return {
    q1EffectiveStartDate: toTitleCaseSentence(
      getMonthAbbreviation(
        businessEntity?.featureSpecificInfo?.compliance?.complianceCalendar
          ?.quarters?.Q1?.effectiveStartDate
      )
    ),
    q1EffectiveEndDate: toTitleCaseSentence(
      getMonthAbbreviation(
        businessEntity?.featureSpecificInfo?.compliance?.complianceCalendar
          ?.quarters?.Q1?.effectiveEndDate
      )
    ),
    q2EffectiveStartDate: toTitleCaseSentence(
      getMonthAbbreviation(
        businessEntity?.featureSpecificInfo?.compliance?.complianceCalendar
          ?.quarters?.Q2?.effectiveStartDate
      )
    ),
    q2EffectiveEndDate: toTitleCaseSentence(
      getMonthAbbreviation(
        businessEntity?.featureSpecificInfo?.compliance?.complianceCalendar
          ?.quarters?.Q2?.effectiveEndDate
      )
    ),
    q3EffectiveStartDate: toTitleCaseSentence(
      getMonthAbbreviation(
        businessEntity?.featureSpecificInfo?.compliance?.complianceCalendar
          ?.quarters?.Q3?.effectiveStartDate
      )
    ),
    q3EffectiveEndDate: toTitleCaseSentence(
      getMonthAbbreviation(
        businessEntity?.featureSpecificInfo?.compliance?.complianceCalendar
          ?.quarters?.Q3?.effectiveEndDate
      )
    ),
    q4EffectiveStartDate: toTitleCaseSentence(
      getMonthAbbreviation(
        businessEntity?.featureSpecificInfo?.compliance?.complianceCalendar
          ?.quarters?.Q4?.effectiveStartDate
      )
    ),
    q4EffectiveEndDate: toTitleCaseSentence(
      getMonthAbbreviation(
        businessEntity?.featureSpecificInfo?.compliance?.complianceCalendar
          ?.quarters?.Q4?.effectiveEndDate
      )
    ),
  };
}
